import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Mail } from "lucide-react";
import { sendOtp, verifyOtp } from "../../../api/auth";

const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 60; // seconds before resend is allowed

/**
 * OtpVerification step component
 * Props:
 *   email       {string}  — the email to verify (shown to user, sent OTP to)
 *   onVerified  {fn}      — called when OTP is confirmed; passes { otpVerified: true }
 *   onBack      {fn}      — navigate back to the previous step (edit email)
 */
export default function OtpVerification({ email, onVerified, onBack }) {
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(""));
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN);
  const [resendLoading, setResendLoading] = useState(false);
  const [verified, setVerified] = useState(false);
  const inputRefs = useRef([]);

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
    setFocusedIndex(0);
  }, []);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleVerify = useCallback(
    async (otp) => {
      if (!otp || otp.length < OTP_LENGTH) {
        setError("Please enter the complete 6-digit code.");
        const firstEmpty = digits.findIndex((d) => !d);
        inputRefs.current[firstEmpty !== -1 ? firstEmpty : 0]?.focus();
        return;
      }
      setLoading(true);
      setError("");
      try {
        const res = await verifyOtp(email, otp);
        if (res.verified) {
          setVerified(true);
          // Brief success flash, then proceed
          setTimeout(() => {
            onVerified({ otpVerified: true });
          }, 600);
        }
      } catch (err) {
        setError(err.message || "Incorrect OTP. Please try again.");
        setDigits(Array(OTP_LENGTH).fill(""));
        setFocusedIndex(0);
        inputRefs.current[0]?.focus();
      } finally {
        setLoading(false);
      }
    },
    [email, onVerified, digits]
  );

  // Auto-submit when all digits filled
  useEffect(() => {
    if (digits.every((d) => d !== "") && !loading && !verified) {
      handleVerify(digits.join(""));
    }
  }, [digits, loading, verified, handleVerify]);

  const maskedEmail = useMemo(() => {
    if (!email) return "";
    const parts = email.split("@");
    if (parts.length !== 2) return email;
    const [local, domain] = parts;
    if (local.length <= 3) {
      return `${local[0]}***@${domain}`;
    }
    const visible = local.slice(0, 3);
    const masked = "*".repeat(Math.max(local.length - 3, 5));
    return `${visible}${masked}@${domain}`;
  }, [email]);

  const handleDigitChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    setError("");

    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);

    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
      setFocusedIndex(index + 1);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (digits[index]) {
        const newDigits = [...digits];
        newDigits[index] = "";
        setDigits(newDigits);
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
        setFocusedIndex(index - 1);
        const newDigits = [...digits];
        newDigits[index - 1] = "";
        setDigits(newDigits);
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
      setFocusedIndex(index - 1);
    } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
      setFocusedIndex(index + 1);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!pasted) return;
    const newDigits = [...digits];
    pasted.split("").forEach((char, i) => {
      newDigits[i] = char;
    });
    setDigits(newDigits);
    const nextIdx = Math.min(pasted.length, OTP_LENGTH - 1);
    inputRefs.current[nextIdx]?.focus();
    setFocusedIndex(nextIdx);
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || resendLoading) return;
    setResendLoading(true);
    setError("");
    setDigits(Array(OTP_LENGTH).fill(""));
    setFocusedIndex(0);
    try {
      await sendOtp(email);
      setResendCooldown(RESEND_COOLDOWN);
      inputRefs.current[0]?.focus();
    } catch (err) {
      setError(err.message || "Failed to resend OTP. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center w-full max-w-[380px] mx-auto text-center py-2 sm:py-4">
      {/* Top Envelope Icon Badge */}
      <div className="w-20 h-20 rounded-full bg-[#FFEFE9] flex items-center justify-center shrink-0 mb-6">
        <Mail className="w-9 h-9 text-[#FF4C18]" strokeWidth={2} />
      </div>

      {/* Title */}
      <h2 className="text-2xl sm:text-[26px] font-bold text-gray-900 tracking-tight">
        Verify your email
      </h2>

      {/* Subtitle */}
      <p className="text-xs sm:text-[14px] text-gray-500 mt-2.5 max-w-[360px] leading-relaxed">
        We sent a 6-digit code to{" "}
        <span className="font-semibold text-gray-900">{maskedEmail || email}</span>.
      </p>

      {/* OTP Input Grid */}
      <div
        className="flex items-center justify-between gap-2.5 sm:gap-3 w-full max-w-[340px] sm:max-w-[360px] mt-6"
        onPaste={handlePaste}
      >
        {digits.map((digit, index) => {
          const isFocused = focusedIndex === index;
          return (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              id={`otp-digit-${index}`}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={1}
              value={digit}
              onFocus={() => setFocusedIndex(index)}
              onBlur={() => setFocusedIndex((prev) => (prev === index ? null : prev))}
              onChange={(e) => handleDigitChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              disabled={loading || verified}
              className={`
                w-11 h-12 sm:w-12 sm:h-12 text-center text-xl font-bold rounded-xl
                transition-colors outline-none
                ${
                  isFocused
                    ? "border-2 border-[#FF4C18] bg-white text-gray-900"
                    : digit
                    ? "border border-gray-300 bg-white text-gray-900"
                    : "border border-gray-200 bg-[#F9FAFB] text-gray-900"
                }
                ${error ? "border-red-400 bg-red-50 text-red-700" : ""}
                ${verified ? "border-green-500 bg-green-50 text-green-700" : ""}
                ${loading || verified ? "opacity-75 cursor-not-allowed" : ""}
              `}
            />
          );
        })}
      </div>

      {/* Error Message */}
      {error && (
        <p className="text-red-500 text-xs sm:text-sm font-medium text-center mt-3 animate-fadeIn">
          {error}
        </p>
      )}

      {/* Verified Success Message */}
      {verified && !error && (
        <p className="text-green-600 text-xs sm:text-sm font-medium text-center mt-3 animate-fadeIn">
          Email verified successfully! Proceeding...
        </p>
      )}

      {/* Verify Button */}
      <button
        type="button"
        onClick={() => handleVerify(digits.join(""))}
        disabled={loading || verified}
        className="w-full max-w-[340px] sm:max-w-[360px] mt-6 h-12 bg-[#FF4C18] hover:bg-[#E63E0F] text-white font-semibold text-base rounded-xl transition-colors shadow-sm flex items-center justify-center gap-2 cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
      >
        {loading ? (
          <>
            <svg
              className="animate-spin h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>
            <span>Verifying...</span>
          </>
        ) : (
          "Verify"
        )}
      </button>

      {/* Resend Cooldown / Button */}
      <div className="flex items-center justify-center gap-1.5 text-xs sm:text-[13px] text-gray-700 mt-4 sm:mt-5 text-center">
        <span>Didn't receive the code?</span>
        {resendCooldown > 0 ? (
          <span>
            Resend in <span className="font-semibold text-[#FF4C18]">{resendCooldown}s</span>
          </span>
        ) : (
          <button
            type="button"
            onClick={handleResend}
            disabled={resendLoading}
            className="text-[#FF4C18] font-semibold hover:underline cursor-pointer disabled:opacity-50"
          >
            {resendLoading ? "Sending..." : "Resend"}
          </button>
        )}
      </div>

      {/* Change Email Link */}
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="flex items-center justify-center gap-1.5 text-xs sm:text-[13px] text-gray-500 hover:text-gray-800 transition-colors mt-3 cursor-pointer"
        >
          <span className="text-[#FF4C18] font-semibold text-sm">←</span>
          <span>Change email address</span>
        </button>
      )}
    </div>
  );
}
